import { LocationSource } from '@carvana/merch-geolocation';
import { useContext, useMemo } from 'react';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import { useSelectedTrade } from '~/context/common/TradeContext';
import UserContext, { useUserLocation } from '~/context/common/UserContext';
import { useFinanceInfo } from '~/context/srp/FinanceContext';
import {
  useInventoryVehicles,
  usePagination,
  useRequestIds
} from '~/context/srp/InventoryContext';
import { useTradeInfo } from '~/context/srp/SearchContext';
import SearchEventsDataContext, {
  useRefSource,
  useSearchSource
} from '~/context/srp/SearchEventsDataContext';
import { MegaEventSRPUserInfo } from '~/contracts/srp';
import useGetSortByChanged from '~/hooks/srp/useGetSortByChanged';
import replaceUndefinedWithNull from '~/utilities/replaceUndefinedWithNull';

export default function useGetUserTileInfo() {
  const { appliedFilters, sortBy } = useContext(AppliedFiltersContext);
  const { keywordSuggestion } = useContext(SearchEventsDataContext);

  const { isUsingTerms2: ignoredIsUsingTerms2, ...financeInfo } =
    useFinanceInfo();
  const { searchRequestId, previousSearchRequestId } = useRequestIds();
  const { currentPage, totalMatchedInventory = 0 } = usePagination();
  const inventoryVehicles = useInventoryVehicles();
  const currentPageVehicleCount = inventoryVehicles.length;
  const { accuracyRadius, isFromCookies, source, zip } = useUserLocation();
  const refSource = useRefSource();
  const searchSource = useSearchSource();
  const tradeInfo = useTradeInfo();
  const selectedTrade = useSelectedTrade();
  const { maxTradeTaxSavings, tradeIncentive } = selectedTrade ?? {};
  const isInitialSortBy = useGetSortByChanged();
  const { geolocationLogging: { merchLocationData = null } = {} } =
    useContext(UserContext);

  const isDefaultFilters = Object.keys(appliedFilters).length === 0;
  const isOpenSearch = isDefaultFilters && isInitialSortBy;

  return useMemo<MegaEventSRPUserInfo>(
    () =>
      replaceUndefinedWithNull({
        appliedFilters,
        currentPage,
        currentPageVehicleCount,
        financeInfo,
        geoIpAccuracyRadius:
          source === 'ipaddress'
            ? accuracyRadius
            : source === LocationSource.UtmLocation
            ? merchLocationData?.accuracyRadius
            : null,
        isOpenSearch,
        isZipFromCookies: isFromCookies,
        previousSearchRequestId,
        refSource,
        searchRequestId,
        searchSource,
        sortBy,
        suggestionInfo: {
          action: keywordSuggestion?.action ?? null,
          buildKey: keywordSuggestion?.buildKey ?? null,
          description: keywordSuggestion?.description ?? null,
          isStickySearchbar: keywordSuggestion?.isStickySearchbar ?? null,
          suggestionIndex: keywordSuggestion?.suggestionIndex ?? null,
          suggestionsLength: keywordSuggestion?.suggestionsLength ?? null,
          suggestionSource: keywordSuggestion?.suggestionSource ?? null,
          suggestionText: keywordSuggestion?.suggestionText ?? null,
          textEntered: keywordSuggestion?.textEntered ?? null
        },
        totalMatchedInventory,
        tradesInfo: {
          applyTradeIn: tradeInfo?.applyTradeIn,
          maxTradeTaxSavings: maxTradeTaxSavings ?? null,
          status:
            tradeInfo?.status && tradeInfo?.totalTradeInValue === 0
              ? 'offer pending'
              : tradeInfo?.status,
          totalTradeInEquity: tradeInfo?.totalTradeInEquity,
          totalTradeInValue: tradeInfo?.totalTradeInValue,
          tradeIncentive: tradeIncentive ?? null
        },
        zip: zip,
        zipFromIp: merchLocationData?.zip,
        zipSource: source
      }),
    [
      accuracyRadius,
      appliedFilters,
      currentPage,
      currentPageVehicleCount,
      financeInfo,
      isFromCookies,
      isOpenSearch,
      keywordSuggestion,
      maxTradeTaxSavings,
      merchLocationData?.accuracyRadius,
      merchLocationData?.zip,
      previousSearchRequestId,
      refSource,
      searchRequestId,
      searchSource,
      sortBy,
      source,
      totalMatchedInventory,
      tradeIncentive,
      tradeInfo,
      zip
    ]
  );
}
